import React, { useState } from "react";
import { Box, Typography, Paper, alpha, useTheme } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";

/**
 * LeftNavigation Component
 *
 * Reusable left navigation sidebar for multi-section pages
 *
 * @param {Object} props
 * @param {Array} props.navItems - Navigation items array with structure:
 *   [{ id, label, icon, disabled, description }]
 * @param {string} props.activeSection - Currently active section id
 * @param {Function} props.onSectionChange - Callback when section changes (sectionId) => void
 * @param {boolean} [props.showTooltips] - Whether to show info tooltips on hover (default: false)
 * @param {number} [props.width] - Width of the navigation sidebar (default: 250)
 */
const LeftNavigation = ({
  navItems = [],
  activeSection,
  onSectionChange,
  showTooltips = false,
  width = 250,
}) => {
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {};
  const perlTypography = theme.custom?.perlTypography || {};
  const brandColor = perlColors.brandColor || "#ff6034";

  const [hoveredNav, setHoveredNav] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <>
      {/* Left Navigation Sidebar */}
      <Box
        sx={{
          width: width,
          borderRight: `1px solid ${perlColors.border}`,
          bgcolor: perlColors.surface,
          display: "flex",
          flexDirection: "column",
          py: 2,
          overflow: "auto",
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: perlColors.border,
            borderRadius: "3px",
          },
        }}
      >
        {/* Navigation Items */}
        {navItems.map((item, index) => (
          <React.Fragment key={item.id}>
            <Box
              onClick={() => !item.disabled && onSectionChange(item.id)}
              sx={{
                px: 1,
                py: 2,
                cursor: item.disabled ? "not-allowed" : "pointer",
                bgcolor: activeSection === item.id ? alpha(perlColors.white, 0.08) : "transparent",
                borderLeft:
                  activeSection === item.id ? `4px solid ${brandColor}` : "4px solid transparent",
                display: "flex",
                alignItems: "center",
                gap: 2,
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                opacity: item.disabled ? 0.4 : 1,
                "&:hover": {
                  bgcolor: item.disabled
                    ? "transparent"
                    : alpha(perlColors.white, activeSection === item.id ? 0.12 : 0.04),
                },
              }}
            >
              <Box
                sx={{
                  fontSize: "1.5rem",
                  lineHeight: 1,
                  filter: item.disabled ? "grayscale(100%)" : "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color:
                    activeSection === item.id ? perlColors.textPrimary : perlColors.textSecondary,
                }}
              >
                {item.icon}
              </Box>
              <Typography
                sx={{
                  fontSize: "0.9375rem",
                  fontWeight: activeSection === item.id ? 600 : 400,
                  color:
                    activeSection === item.id ? perlColors.textPrimary : perlColors.textSecondary,
                  fontFamily: perlTypography.family,
                  letterSpacing: "0.3px",
                  lineHeight: 1.4,
                  flex: 1,
                }}
              >
                {item.label}
              </Typography>
            </Box>
            {index < navItems.length - 1 && (
              <Box
                sx={{
                  height: "1px",
                  background: `linear-gradient(to right, transparent, ${alpha(
                    perlColors.white,
                    0.1
                  )} 20%, ${alpha(perlColors.white, 0.1)} 80%, transparent)`,
                  my: 0.5,
                }}
              />
            )}
          </React.Fragment>
        ))}
        {/* Divider after all navigation items */}
        <Box
          sx={{
            height: "1px",
            background: `linear-gradient(to right, transparent, ${alpha(
              perlColors.white,
              0.1
            )} 20%, ${alpha(perlColors.white, 0.1)} 80%, transparent)`,
            my: 0.5,
          }}
        />
      </Box>
    </>
  );
};

export default LeftNavigation;
